import Head from 'next/head';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import { IntroScreen, GateScreen, QuestionScreen, ResultsScreen, ConfirmScreen } from '@/components/Assessment';
import { calcScore, calcPillarScores, getTier, getTierIndex } from '@/components/Assessment/utils';
import { QUESTIONS } from '@/components/Assessment/constants';
import { Screen, GateFormData } from '@/components/Assessment/types';
import { AssessmentContentProvider } from '@/components/Assessment/content/context';
import { resolveAssessmentContent } from '@/components/Assessment/content/resolve';
import { AssessmentContentInput } from '@/components/Assessment/content/types';

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
  const apiService = new ApiService();
  let cmsContent: AssessmentContentInput = null;
  try {
    cmsContent = (await apiService.getAssessmentContent(preview)) ?? null;
  } catch (err) {
    // Fall back to the built-in copy if Sanity is unavailable so the page
    // never fails to build/render.
    console.error('[assessment] failed to load Sanity content', err);
  }
  return {
    props: { cmsContent },
    revalidate: 3600,
  };
};

interface AssessmentPageProps {
  cmsContent: AssessmentContentInput;
}

const AssessmentPage: React.FC<AssessmentPageProps> = ({ cmsContent }) => {
  const router = useRouter();
  const content = useMemo(() => resolveAssessmentContent(cmsContent), [cmsContent]);
  const [screen, setScreen] = useState<Screen>('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(new Array(QUESTIONS.length).fill(null));
  const [gateData, setGateData] = useState<GateFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    companySize: '',
    role: '',
  });

  const score = calcScore(answers);
  const pillar = calcPillarScores(answers);
  // Display label comes from the (editable) CMS tier labels; the HubSpot
  // payload keeps the canonical `getTier` value (see submitToHubspot).
  const tier = content.tiers[getTierIndex(score)]?.label ?? getTier(score);

  // Sync state with URL when browser back/forward buttons are used
  useEffect(() => {
    const step = router.query.step as string | undefined;

    if (!step) {
      setScreen('intro');
      setCurrentQ(0);
      return;
    }

    const stepNum = parseInt(step, 10);

    if (stepNum >= 1 && stepNum <= 8) {
      setScreen('question');
      setCurrentQ(stepNum - 1);
    } else if (stepNum === 9) {
      setScreen('gate');
    } else if (stepNum === 10) {
      setScreen('results');
    } else if (stepNum === 11) {
      setScreen('confirm');
    }
  }, [router.query.step]);

  const handleIntroStart = () => {
    setCurrentQ(0);
    setAnswers(new Array(QUESTIONS.length).fill(null));
    setScreen('question');
    router.push('?step=1');
  };

  const handleAnswer = (idx: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentQ] = idx;
      return next;
    });
  };

  const submitToHubspot = useCallback(async (finalAnswers: (number | null)[], finalGateData: GateFormData) => {
    const finalScore = calcScore(finalAnswers);
    const finalPillar = calcPillarScores(finalAnswers);
    const finalTier = getTier(finalScore);
    try {
      await fetch('/api/submit-assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...finalGateData,
          score: finalScore,
          tier: finalTier,
          answers: finalAnswers,
          pillar: finalPillar,
        }),
      });
    } catch (err) {
      console.error('[assessment] HubSpot submission error', err);
    }
  }, []);

  const handleNext = () => {
    if (currentQ < QUESTIONS.length - 1) {
      const nextQuestion = currentQ + 1;
      setCurrentQ(nextQuestion);
      router.push(`?step=${nextQuestion + 1}`);
    } else {
      setScreen('gate');
      router.push('?step=9');
    }
  };

  const handleGateSubmit = (data: GateFormData) => {
    setGateData(data);
    setScreen('results');
    router.push('?step=10');
    submitToHubspot(answers, data);
  };

  const handleGateBack = () => {
    setCurrentQ(QUESTIONS.length - 1);
    setScreen('question');
    router.push('?step=8');
  };

  return (
    <AssessmentContentProvider content={content}>
      <Head>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href="https://foxbox.com/ai-native-assessment" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Head>

      <div className="font-sans bg-a-off-white text-a-text-primary min-h-screen">
        {screen === 'intro' && <IntroScreen onStart={handleIntroStart} />}
        {screen === 'question' && (
          <QuestionScreen
            currentQ={currentQ}
            answers={answers}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onBack={() => {
              if (currentQ > 0) {
                const prevQuestion = currentQ - 1;
                setCurrentQ(prevQuestion);
                router.push(`?step=${prevQuestion + 1}`);
              }
            }}
          />
        )}
        {screen === 'gate' && <GateScreen onStart={handleGateSubmit} onBack={handleGateBack} />}
        {screen === 'results' && (
          <ResultsScreen
            score={score}
            tier={tier}
            pillar={pillar}
            firstName={gateData.firstName}
            onCta={() => {
              setScreen('confirm');
              router.push('?step=11');
            }}
          />
        )}
        {screen === 'confirm' && (
          <ConfirmScreen score={score} tier={tier} firstName={gateData.firstName} email={gateData.email} />
        )}
      </div>
    </AssessmentContentProvider>
  );
};

export default AssessmentPage;
